import {
  createNativeStackNavigator,
  NativeStackScreenProps,
} from '@react-navigation/native-stack';
import { ErrorBoundary } from 'components/ErrorBoundary';
import { NavigationStacks, Screens, Stacks } from 'components/Navigation/types';
import { useAlert } from 'hooks/useAlert';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import { Explorer } from 'screens/AssessmentScreen/Explorer/Explorer';
import { SyncIndicator } from 'screens/SyncScreen/SyncIndicator';
import { CLOSE_ASSESSMENT, UNLOAD_ASSESSMENT } from 'store/assessments/actions';
import {
  selectAseAssetId,
  selectAseIsOnline,
  selectAseIsOpen,
  selectAseStatus,
} from 'store/assessments/selectors';
import { ActionScreen } from './ActionScreen';
import { AssetScreenForNavigation } from './AssetScreen';
import { ControlScreen } from './ControlScreen';
import { ExplorerNavigation } from './Explorer/types';
import { FieldsScreenForNavigation } from './FieldsScreen';
import { QuestionScreen } from './QuestionScreen';
import { SectionScreen } from './SectionScreen';
import { SummaryScreen } from './SummaryScreen';
import { QrIndicator } from './QrIndicator';

const Stack = createNativeStackNavigator<NavigationStacks[Stacks.ASSESSMENT]>();

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.ROOT],
  Screens.ROOT_ASSESSMENT
>;

export const AssessmentScreen: React.FC<Props> = ({ navigation }) => {
  const dispatch = useDispatch();
  const alert = useAlert();
  const screenSize = useScreenSize();
  const assetId = useSelector(selectAseAssetId);
  const isOnline = useSelector(selectAseIsOnline);
  const isOpen = useSelector(selectAseIsOpen);
  const status = useSelector(selectAseStatus);

  React.useEffect(() => {
    if (!isOpen) {
      // Close assessment if there is no assessment ID
      navigation.goBack();
    }
  }, [isOpen, status, navigation, dispatch]);

  React.useEffect(() => {
    return () => {
      dispatch(UNLOAD_ASSESSMENT());
    };
  }, [assetId, dispatch]);

  const stack = React.useMemo(
    () =>
      !!isOpen && (
        <Stack.Navigator
          initialRouteName={
            screenSize === 'small' ? Screens.ASE_EXPLORER : Screens.ASE_SUMMARY
          }
          screenOptions={{
            headerShown: false,
            animation: screenSize === 'small' ? 'default' : 'none',
          }}
        >
          {screenSize === 'small' && (
            <Stack.Screen name={Screens.ASE_EXPLORER}>
              {() => (
                <Explorer
                  navigation={navigation as unknown as ExplorerNavigation}
                />
              )}
            </Stack.Screen>
          )}
          <Stack.Screen name={Screens.ASE_SUMMARY} component={SummaryScreen} />
          <Stack.Screen
            name={Screens.ASE_FIELDS}
            component={FieldsScreenForNavigation}
          />
          <Stack.Screen
            name={Screens.ASE_ASSET}
            component={AssetScreenForNavigation}
          />
          <Stack.Screen name={Screens.ASE_SECTION} component={SectionScreen} />
          <Stack.Screen
            name={Screens.ASE_QUESTION}
            component={QuestionScreen}
          />
          <Stack.Screen name={Screens.ASE_ACTION} component={ActionScreen} />
          <Stack.Screen name={Screens.ASE_CONTROL} component={ControlScreen} />
        </Stack.Navigator>
      ),
    [isOpen, navigation, screenSize],
  );

  // useMemo here to gate the rest of the assessment page from
  // re-rendering when anything related to navigation changes
  return React.useMemo(
    () =>
      isOpen ? (
        <ErrorBoundary
          onError={() => {
            alert({
              text: 'The assessment was closed because something went wrong',
            });
            dispatch(CLOSE_ASSESSMENT());
          }}
        >
          {screenSize === 'large' ? (
            <View style={{ flexDirection: 'row', flex: 1 }}>
              <View
                style={{
                  flex: 1,
                  borderColor: '#ddd',
                  borderRightWidth: 1,
                  backgroundColor: '#fff',
                  overflow: 'hidden',
                }}
              >
                <Explorer
                  navigation={navigation as unknown as ExplorerNavigation}
                />
              </View>
              <View
                style={{
                  flex: 1,
                  backgroundColor: '#fff',
                  overflow: 'hidden',
                }}
              >
                {stack}
              </View>
            </View>
          ) : (
            stack
          )}
          <QrIndicator />
          <SyncIndicator showSyncIcon={!isOnline} />
        </ErrorBoundary>
      ) : null,
    [isOpen, screenSize, navigation, stack, isOnline, alert, dispatch],
  );
};
